// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {SpinnerEl} from 'common/components/spinner';
import {SvgIconEl} from 'common/components/svg-icon';

import {SpinnerColor} from '../spinner/types';
import {ExperimentalTooltipEl} from '../tooltip';
import type {Button} from './index';
import {ButtonTheme, ButtonType} from './constants';

export function template(this: Button) {
  const theme = this.attr(`theme`);
  const layout = this.attr(`layout`);
  const isPrimary = theme === ButtonTheme.Primary;
  const content = this.getContent();
  const iconOnly = this.isIconOnly();
  const isDestructive = this.attr(`type`) === ButtonType.Destructive || this.attr(`icon`) === `trashcan`;
  const isPressed = this.attr(`pressed`);
  const isIconHighlighted = this.attr(`highlight-icon`);
  const isIconFilled = this.attr(`fill-icon`);
  const isIconRightHighlighted = this.attr(`highlight-icon-right`);
  const isIconRightFilled = this.attr(`fill-icon-right`);
  const tooltip = this.params.tooltip || this.attr(`tooltip`);

  const displayTooltip = tooltip && !this.attr(`pressed`);

  const linkAttrs = {
    ...(this.attr(`href`) && {href: this.attr(`href`)}),
    ...(this.attr(`target`) && {target: this.attr(`target`)}),
    ...(this.attr(`download`) && {download: this.attr(`download`)}),
  };

  return (
    <a
      sel='.mp-button-container'
      attrs={{...linkAttrs, elref: `buttonContainer`}}
      class={{
        [`mp-button-${theme}`]: true,
        [`mp-button-layout-${layout}`]: true,
        [`type-${this.attr(`type`)}`]: true,
        'type-destructive': isDestructive,
        'is-icon-only': iconOnly,
        'is-pressed': isPressed,
        'is-disabled': this.attr(`disabled`),
        'disable-pressed-styles': this.attr(`disable-pressed-styles`),
        'disabled-stays-opaque': this.attr(`disabled-stays-opaque`),
      }}
      on={{
        click: this.handleClick,
      }}
    >
      <div sel='.mp-button-animation-layer' />
      <div sel='.mp-button-content'>
        {this.attr(`icon`) === `spinner` ? (
          <SpinnerEl
            key='button-spinner'
            attrs={{
              color: isPrimary ? SpinnerColor.White : SpinnerColor.Blue,
            }}
          />
        ) : this.attr(`icon`) ? (
          <SvgIconEl
            key='button-icon'
            attrs={{
              icon: this.attr(`icon`),
              color: this.attr(`icon-color`),
              'has-unread': this.attr(`icon-has-unread`),
            }}
            class={{highlighted: isIconHighlighted, filled: isIconFilled}}
          />
        ) : null}
        {content ? <slot sel='.mp-button-content-text' /> : null}
        {this.attr(`icon-right`) === `spinner` ? (
          <SpinnerEl
            key='button-spinner'
            attrs={{
              color: isPrimary ? SpinnerColor.White : SpinnerColor.Blue,
            }}
          />
        ) : this.attr(`icon-right`) ? (
          <SvgIconEl
            key='button-icon-right'
            attrs={{
              icon: this.attr(`icon-right`),
              color: this.attr(`icon-right-color`),
              'has-unread': this.attr(`icon-has-unread`),
            }}
            class={{right: true, highlighted: isIconRightHighlighted, filled: isIconRightFilled}}
          />
        ) : null}
      </div>
      {displayTooltip && (
        <ExperimentalTooltipEl
          attrs={{
            'delay-in': 10,
            'delay-out': 10,
            ...tooltip,
          }}
          params={{
            text: tooltip.text,
          }}
          props={{parentEl: this}}
        />
      )}
    </a>
  );
}
